import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { allWifiPlans, wifiTvPlans } from '../mock';
import { Wifi, Tv, CheckCircle2, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { trackPlanSelection } from '../utils/analytics';
import useScrollReveal from '../hooks/useScrollReveal';

const Plans = () => {
  const [activeTab, setActiveTab] = useState('wifi');
  useScrollReveal();

  return (
    <div className="min-h-screen bg-[#050505] text-white py-16 relative overflow-hidden" data-testid="plans-page">
      <div className="ambient-glow" />
      <div className="grain-overlay" />

      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <div className="text-center mb-16 reveal-zoom">
          <Badge className="bg-orange-500/10 border border-orange-500/30 text-orange-300 mb-4">
            All Packages
          </Badge>
          <h1 className="text-5xl md:text-6xl font-bold mb-4" data-testid="plans-heading">
            Our <span className="bg-gradient-to-r from-[#FFD27A] to-[#FF5C00] bg-clip-text text-transparent">Plans</span>
          </h1>
          <p className="text-xl text-white/60 max-w-2xl mx-auto">
            Affordable broadband with unlimited data, no FUP, no hidden charges.
          </p>
        </div>

        {/* Plans Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="max-w-6xl mx-auto reveal">
          <TabsList className="grid w-full grid-cols-2 mb-10 bg-white/5 border border-white/10 backdrop-blur-md p-1 rounded-2xl h-auto">
            <TabsTrigger
              value="wifi"
              className="text-base data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#FF8C00] data-[state=active]:to-[#FFB800] data-[state=active]:text-black text-white/70 rounded-xl py-3"
              data-testid="tab-wifi"
            >
              <Wifi className="w-5 h-5 mr-2" />
              WiFi Only Plans
            </TabsTrigger>
            <TabsTrigger
              value="combo"
              className="text-base data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#FF8C00] data-[state=active]:to-[#FFB800] data-[state=active]:text-black text-white/70 rounded-xl py-3"
              data-testid="tab-combo"
            >
              <Tv className="w-5 h-5 mr-2" />
              WiFi + Cable TV
            </TabsTrigger>
          </TabsList>

          {/* WiFi Plans */}
          <TabsContent value="wifi" className="space-y-8">
            <Card className="glass border-0 reveal-zoom" data-testid="wifi-plans-card">
              <CardHeader className="border-b border-white/10">
                <CardTitle className="text-2xl text-center text-white">WiFi Broadband Plans</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/10 text-white/60 uppercase text-xs tracking-wider">
                        <th className="text-left py-4 px-4">Speed</th>
                        <th className="text-center py-4 px-4">Monthly</th>
                        <th className="text-center py-4 px-4">3 Months</th>
                        <th className="text-center py-4 px-4">6 Months</th>
                        <th className="text-center py-4 px-4">10+2 Months</th>
                        <th className="text-center py-4 px-4">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allWifiPlans.map((plan, idx) => (
                        <tr
                          key={idx}
                          className={`border-b border-white/5 hover:bg-orange-500/5 transition-colors ${
                            plan.highlighted ? 'bg-orange-500/10' : ''
                          }`}
                          data-testid={`wifi-plan-row-${idx}`}
                        >
                          <td className="py-4 px-4">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-lg text-[#FFB347]">{plan.speed}</span>
                              {plan.highlighted && (
                                <Badge className="bg-gradient-to-r from-[#FF8C00] to-[#FFB800] text-black text-xs">
                                  Popular
                                </Badge>
                              )}
                            </div>
                          </td>
                          <td className="text-center py-4 px-4 text-white/80">₹{plan.monthly}</td>
                          <td className="text-center py-4 px-4 text-white/80">₹{plan.threeMonth}</td>
                          <td className="text-center py-4 px-4 text-white/80">₹{plan.sixMonth}</td>
                          <td className="text-center py-4 px-4 text-white/80">₹{plan.tenPlusTwo}</td>
                          <td className="text-center py-4 px-4">
                            <Link to="/contact">
                              <Button
                                size="sm"
                                className="bg-gradient-to-r from-[#FF8C00] to-[#FFB800] hover:from-[#FF7700] hover:to-[#FFA600] text-black font-semibold"
                                onClick={() => trackPlanSelection(plan.speed + ' WiFi Plan', plan.monthly)}
                                data-testid={`wifi-subscribe-${idx}`}
                              >
                                Subscribe
                              </Button>
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="mt-6 p-4 glass-orange rounded-xl">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-orange-300 mt-0.5 flex-shrink-0" />
                    <div className="space-y-1">
                      <p className="font-semibold text-white">All WiFi Plans Include:</p>
                      <ul className="text-sm text-white/70 space-y-1">
                        <li>• Unlimited Data (No FUP)</li>
                        <li>• High-Speed Connection</li>
                        <li>• 24/7 Customer Support</li>
                        <li>• Free Installation</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* WiFi + TV Combo Plans */}
          <TabsContent value="combo" className="space-y-8">
            <Card className="glass border-0 reveal-zoom" data-testid="combo-plans-card">
              <CardHeader className="border-b border-white/10">
                <CardTitle className="text-2xl text-center bg-gradient-to-r from-[#FFD27A] to-[#FF5C00] bg-clip-text text-transparent">
                  WiFi + Cable TV Combo Plans
                </CardTitle>
                <p className="text-center text-white/60 mt-2">350+ Live TV Channels Available</p>
              </CardHeader>
              <CardContent className="p-6">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/10 text-white/60 uppercase text-xs tracking-wider">
                        <th className="text-left py-4 px-4">Speed + TV</th>
                        <th className="text-center py-4 px-4">Monthly</th>
                        <th className="text-center py-4 px-4">3 Months</th>
                        <th className="text-center py-4 px-4">6 Months</th>
                        <th className="text-center py-4 px-4">10+2 Months</th>
                        <th className="text-center py-4 px-4">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {wifiTvPlans.map((plan, idx) => (
                        <tr
                          key={idx}
                          className={`border-b border-white/5 hover:bg-orange-500/5 transition-colors ${
                            plan.highlighted ? 'bg-orange-500/10' : ''
                          }`}
                          data-testid={`combo-plan-row-${idx}`}
                        >
                          <td className="py-4 px-4">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-lg text-[#FFB347]">{plan.speed}</span>
                              {plan.highlighted && (
                                <Badge className="bg-gradient-to-r from-[#FF8C00] to-[#FFB800] text-black text-xs">
                                  Best Value
                                </Badge>
                              )}
                            </div>
                          </td>
                          <td className="text-center py-4 px-4 text-white/80">₹{plan.monthly}</td>
                          <td className="text-center py-4 px-4 text-white/80">₹{plan.threeMonth}</td>
                          <td className="text-center py-4 px-4 text-white/80">₹{plan.sixMonth}</td>
                          <td className="text-center py-4 px-4 text-white/80">₹{plan.tenPlusTwo}</td>
                          <td className="text-center py-4 px-4">
                            <Link to="/contact">
                              <Button
                                size="sm"
                                className="bg-gradient-to-r from-[#FF8C00] to-[#FFB800] hover:from-[#FF7700] hover:to-[#FFA600] text-black font-semibold"
                                onClick={() => trackPlanSelection(plan.speed + ' Combo Plan', plan.monthly)}
                                data-testid={`combo-subscribe-${idx}`}
                              >
                                Subscribe
                              </Button>
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="mt-6 p-4 glass-orange rounded-xl">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-orange-300 mt-0.5 flex-shrink-0" />
                    <div className="space-y-1">
                      <p className="font-semibold text-white text-lg">All Combo Plans Include:</p>
                      <ul className="text-sm text-white/70 space-y-1">
                        <li>• Unlimited High-Speed Internet</li>
                        <li>• 350+ Live TV Channels</li>
                        <li>• Free Installation & Set-top Box</li>
                        <li>• Easy to Approach Service</li>
                        <li>• Quick Connection within 24-48 hours</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* CTA */}
        <div className="text-center mt-16 reveal">
          <Card className="max-w-2xl mx-auto glass-orange border-0" data-testid="plans-cta">
            <CardContent className="p-8">
              <h3 className="text-3xl font-bold text-white mb-4">Need Help Choosing?</h3>
              <p className="text-white/70 mb-6">Our team can help you find the perfect plan for your home or office.</p>
              <Link to="/contact">
                <Button
                  size="lg"
                  className="bg-gradient-to-r from-[#FF8C00] to-[#FFB800] hover:from-[#FF7700] hover:to-[#FFA600] text-black font-semibold px-10"
                  data-testid="plans-contact-cta-btn"
                >
                  Contact Us Now
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Plans;
